import { useFragment, graphql } from 'react-relay';

import { useShowPricingGuidance } from 'dibs-pricing-guidance/exports/useShowPricingGuidance.ts';

import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import { type usePdpEstimateSimilarSold_viewer$key } from './__generated__/usePdpEstimateSimilarSold_viewer.graphql.ts';
import { type usePdpEstimateSimilarSold_item$key } from './__generated__/usePdpEstimateSimilarSold_item.graphql.ts';

const viewerFragment = graphql`
    fragment usePdpEstimateSimilarSold_viewer on Viewer {
        similarSoldItemsResults: similarSoldItems(itemId: $itemId, first: 0)
            @include(if: $isEstimateSimilarSoldVariant) {
            totalResults
        }
    }
`;

const itemFragment = graphql`
    fragment usePdpEstimateSimilarSold_item on Item {
        ...useShowPricingGuidance_item
    }
`;

const MIN_SIMILAR_SOLD_ITEMS = 3;

export const useHasEnoughSimilarSoldItems = ({
    viewer: viewerRef,
}: {
    viewer: usePdpEstimateSimilarSold_viewer$key | null;
}): boolean => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const totalResults = viewer?.similarSoldItemsResults?.totalResults || 0;

    return totalResults >= MIN_SIMILAR_SOLD_ITEMS;
};

export const useShowPdpEstimateInTab = ({
    viewer,
    item: itemRef,
    currency,
}: {
    viewer: usePdpEstimateSimilarSold_viewer$key | null;
    item?: usePdpEstimateSimilarSold_item$key | null;
    currency: string;
}): boolean => {
    const item = useFragment(itemFragment, itemRef);

    const { isPdpEstimateSimilarSoldTestVariant } = useServerVarsContext();

    const showPricingGuidance = useShowPricingGuidance({ item: item || null, currency });
    const hasEnoughSimilarSoldItems = useHasEnoughSimilarSoldItems({ viewer });

    return (
        showPricingGuidance && (!isPdpEstimateSimilarSoldTestVariant || !hasEnoughSimilarSoldItems)
    );
};

export const useShowPdpEstimateSimilarSoldSection = ({
    viewer,
    item: itemRef,
    currency,
}: {
    viewer: usePdpEstimateSimilarSold_viewer$key | null;
    item: usePdpEstimateSimilarSold_item$key | null;
    currency: string;
}): boolean => {
    const item = useFragment(itemFragment, itemRef);

    const showPricingGuidance = useShowPricingGuidance({ item: item || null, currency });
    const hasEnoughSimilarSoldItems = useHasEnoughSimilarSoldItems({ viewer });

    return showPricingGuidance && hasEnoughSimilarSoldItems;
};
